import React, { useEffect, useState } from "react";
import { Format } from "@/lib/utils/format";
import { requestContext } from "@/stores/common/request-context";
import { PersonStore } from "@/stores/person-store.core";
import { Card, Flex, Typography, useI18nContext } from "@procore/core-react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { authManager } from "@/lib/managers/auth-manager";
import { PermissionLevel } from "@/models/permission-level";

import type { GetHomeInfoResponse } from "@laborchart-modules/lc-core-api/dist/api/people/get-home-info";

const PRIVACY_SETTINGS_CONFIRMED = "privacy-settings-confirmed";

type UserHomeInfo = GetHomeInfoResponse["data"];

const getUserHomeInfo = async (): Promise<UserHomeInfo> => {
   const result = await PersonStore.getUserHomeInfo().payload;
   return result.data;
};

const useGetUserHomeInfo = (): UseQueryResult<UserHomeInfo, Error> =>
   useQuery({
      queryKey: ["user-home-info"],
      queryFn: getUserHomeInfo,
   });

export const CompanyCard = () => {
   const I18n = useI18nContext();
   const { data: userHomeInfo, error } = useGetUserHomeInfo();
   const [canViewPeople, setCanViewPeople] = useState(false);
   const [canViewProjects, setCanViewProjects] = useState(false);
   const [companyName, setCompanyName] = useState("");
   const [groupCount, setGroupCount] = useState("...");
   const [peopleCount, setPeopleCount] = useState("...");
   const [projectCount, setProjectCount] = useState("...");

   useEffect(() => {
      setCanViewPeople(authManager.checkAuthAction(PermissionLevel.Action.VIEW_PEOPLE));
      setCanViewProjects(authManager.checkAuthAction(PermissionLevel.Action.VIEW_PROJECT));
      if (userHomeInfo && requestContext.usingProcoreHostApp) {
         window.localStorage.setItem(PRIVACY_SETTINGS_CONFIRMED, "true");
      }
      if (userHomeInfo) {
         setCompanyName(userHomeInfo?.companyName || "");
         setGroupCount(
            userHomeInfo?.groupCount !== undefined && userHomeInfo.groupCount !== null
               ? Format.formatNumber(userHomeInfo.groupCount)
               : "...",
         );
         setPeopleCount(
            userHomeInfo?.peopleCount !== undefined && userHomeInfo.peopleCount !== null
               ? Format.formatNumber(userHomeInfo.peopleCount)
               : "...",
         );
         setProjectCount(
            userHomeInfo?.projectCount !== undefined && userHomeInfo.projectCount !== null
               ? Format.formatNumber(userHomeInfo.projectCount)
               : "...",
         );
      }
   }, [userHomeInfo]);

   useEffect(() => {
      if (error) {
         console.error("Error getHomeInfo: ", error);
      }
   }, [error]);

   return (
      <Card
         style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "0.5rem",
            padding: "1rem",
            alignSelf: "stretch",
            borderRadius: "0.5rem",
         }}
      >
         <Typography intent="h2" as="h2">
            {companyName}
         </Typography>
         <Flex direction="row" gap="1rem" alignItems="flex-start" alignSelf="stretch">
            <Flex direction="column" alignItems="flex-start" flex="1 0 0" alignSelf="stretch">
               <Typography
                  intent="small"
                  color="gray45"
                  style={{ alignSelf: "stretch", whiteSpace: "nowrap" }}
               >
                  {I18n.t("views.company.workforce_planning.home.company_info.groups")}
               </Typography>
               <Typography intent="h3" as="h3">
                  {groupCount}
               </Typography>
            </Flex>
            {canViewPeople && (
               <Flex direction="column" alignItems="flex-start" flex="1 0 0" alignSelf="stretch">
                  <Typography
                     intent="small"
                     color="gray45"
                     style={{ alignSelf: "stretch", whiteSpace: "nowrap" }}
                  >
                     {I18n.t("views.company.workforce_planning.home.company_info.people")}
                  </Typography>
                  <Typography intent="h3" as="h3">
                     {peopleCount}
                  </Typography>
               </Flex>
            )}
            {canViewProjects && (
               <Flex direction="column" alignItems="flex-start" flex="1 0 0" alignSelf="stretch">
                  <Typography
                     intent="small"
                     color="gray45"
                     style={{ alignSelf: "stretch", whiteSpace: "nowrap" }}
                  >
                     {I18n.t("views.company.workforce_planning.home.company_info.projects")}
                  </Typography>
                  <Typography intent="h3" as="h3">
                     {projectCount}
                  </Typography>
               </Flex>
            )}
         </Flex>
      </Card>
   );
};
